import { useCallback, useEffect, useMemo, useState } from "react";
import SelectInput from "../../../../components/inputs/selectInput/SelectInput";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import { fetchRoomInformationEfficiencyAPI } from "../../../../features/tables/tablesAPI";
import styles from "./roomInformationEfficiency.module.css";

const EMPTY_DATA = {
  selected: null,
  tournamentOptions: [],
  summary: {
    totalRooms: 0,
    roomsWithInformation: 0,
    roomsWithoutInformation: 0,
    earlyCount: 0,
    onTimeCount: 0,
    lateCount: 0,
  },
  rows: [],
};

const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const numberFormatter = new Intl.NumberFormat("ar-EG", {
  maximumFractionDigits: 0,
});

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "—";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  return dateTimeFormatter.format(parsedDate).replace(",", "");
};

const formatTournamentOptionLabel = (option = {}) => {
  const name = String(option?.name || "بطولة").trim() || "بطولة";
  const serialNumber = Number(option?.serialNumber || 0);

  if (serialNumber > 0) {
    return `#${formatNumber(serialNumber)} - ${name}`;
  }

  return name;
};

const formatDeltaLabel = (deltaSeconds) => {
  if (deltaSeconds === null || deltaSeconds === undefined) return "—";

  const numericSeconds = Number(deltaSeconds);
  if (Number.isNaN(numericSeconds)) return "—";

  const sign = numericSeconds > 0 ? "+" : numericSeconds < 0 ? "-" : "";
  const absoluteSeconds = Math.abs(numericSeconds);
  const minutes = Math.floor(absoluteSeconds / 60);
  const seconds = absoluteSeconds % 60;
  const parts = [];

  if (minutes > 0) {
    parts.push(`${formatNumber(minutes)} د`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(`${formatNumber(seconds)} ث`);
  }

  return `${sign}${parts.join(" ")}`;
};

const getStatusLabel = (status) => {
  if (status === "early") return "مبكر";
  if (status === "on_time") return "في الوقت";
  if (status === "late") return "متأخر";
  return "بدون معلومات";
};

const STATUS_FILTER_OPTIONS = [
  { value: "early", label: "مبكر" },
  { value: "on_time", label: "في الوقت" },
  { value: "late", label: "متأخر" },
];

const getTableTypeLabel = (tableType) => {
  if (tableType === "qualification") return "تأهيل";
  if (tableType === "nextStage") return "مرحلة تالية";
  if (tableType === "winners") return "الفائزين";
  if (tableType === "final") return "نهائي";
  return "غير محدد";
};

const RoomInformationEfficiency = () => {
  const [data, setData] = useState(EMPTY_DATA);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [fetchStatus, setFetchStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [tableNameFilter, setTableNameFilter] = useState("");
  const [executorNameFilter, setExecutorNameFilter] = useState("");
  const [setNumberFilter, setSetNumberFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadData = useCallback(async (tournamentId) => {
    setFetchStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetchRoomInformationEfficiencyAPI(tournamentId);
      const nextData = response?.data || EMPTY_DATA;

      setData({
        selected: nextData?.selected || null,
        tournamentOptions: Array.isArray(nextData?.tournamentOptions)
          ? nextData.tournamentOptions
          : [],
        summary: {
          ...EMPTY_DATA.summary,
          ...(nextData?.summary || {}),
        },
        rows: Array.isArray(nextData?.rows) ? nextData.rows : [],
      });
      setSelectedTournamentId(nextData?.selected?.tournamentId || "");
      setFetchStatus("succeeded");
    } catch (error) {
      setFetchStatus("failed");
      setErrorMessage(
        error.response?.data?.message || "تعذر تحميل بيانات كفاءة الغرف حالياً.",
      );
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTournamentChange = (nextValue) => {
    const normalizedValue = String(nextValue || "").trim();
    setSelectedTournamentId(normalizedValue);
    setTableNameFilter("");
    setExecutorNameFilter("");
    setSetNumberFilter("");
    setStatusFilter("");
    loadData(normalizedValue || undefined);
  };

  const summaryCards = useMemo(
    () => [
      {
        key: "totalRooms",
        label: "إجمالي الغرف",
        value: data.summary.totalRooms,
      },
      {
        key: "roomsWithInformation",
        label: "تمت إضافة المعلومات",
        value: data.summary.roomsWithInformation,
      },
      {
        key: "roomsWithoutInformation",
        label: "لم تتم الإضافة",
        value: data.summary.roomsWithoutInformation,
      },
      {
        key: "earlyCount",
        label: "إضافة مبكرة",
        value: data.summary.earlyCount,
      },
      {
        key: "onTimeCount",
        label: "في الوقت",
        value: data.summary.onTimeCount,
      },
      {
        key: "lateCount",
        label: "إضافة متأخرة",
        value: data.summary.lateCount,
      },
    ],
    [data.summary],
  );

  const tableNameOptions = useMemo(
    () =>
      [...new Set((Array.isArray(data.rows) ? data.rows : []).map((row) => String(row?.tableName || "").trim()))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "ar")),
    [data.rows],
  );

  const executorNameOptions = useMemo(
    () =>
      [
        ...new Set(
          (Array.isArray(data.rows) ? data.rows : [])
            .flatMap((row) =>
              Array.isArray(row?.coLeaderNames) ? row.coLeaderNames : [],
            )
            .map((name) => String(name || "").trim()),
        ),
      ]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "ar")),
    [data.rows],
  );

  const setNumberOptions = useMemo(
    () =>
      [...new Set((Array.isArray(data.rows) ? data.rows : []).map((row) => Number(row?.setNumber || 0)))]
        .filter((value) => Number.isFinite(value) && value > 0)
        .sort((a, b) => a - b),
    [data.rows],
  );

  const filteredRows = useMemo(() => {
    const normalizedTableNameFilter = String(tableNameFilter || "").trim();
    const normalizedExecutorNameFilter = String(executorNameFilter || "").trim();
    const normalizedSetNumberFilter = String(setNumberFilter || "").trim();
    const normalizedStatusFilter = String(statusFilter || "").trim();

    return (Array.isArray(data.rows) ? data.rows : []).filter((row) => {
      if (normalizedTableNameFilter) {
        const tableName = String(row?.tableName || "").trim();
        if (tableName !== normalizedTableNameFilter) {
          return false;
        }
      }

      if (normalizedExecutorNameFilter) {
        const executorNames = Array.isArray(row?.coLeaderNames)
          ? row.coLeaderNames.map((name) => String(name || "").trim())
          : [];
        if (!executorNames.includes(normalizedExecutorNameFilter)) {
          return false;
        }
      }

      if (normalizedSetNumberFilter) {
        if (String(row?.setNumber || "") !== normalizedSetNumberFilter) {
          return false;
        }
      }

      if (normalizedStatusFilter) {
        if (String(row?.status || "") !== normalizedStatusFilter) {
          return false;
        }
      }

      return true;
    });
  }, [data.rows, executorNameFilter, setNumberFilter, statusFilter, tableNameFilter]);

  const hasTournamentOptions = data.tournamentOptions.length > 0;
  const hasRows = data.rows.length > 0;
  const hasFilteredRows = filteredRows.length > 0;
  const isInitialLoading =
    fetchStatus === "loading" && !hasTournamentOptions && !hasRows;

  return (
    <SettingsPagesContainer
      title="كفاءة إضافة معلومات الغرف"
      fullWidth
      fullHeight
      displayContainerClassName={styles.pageDisplayContainer}
    >
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarText}>
            <div className={styles.pageTitle}>مقارنة وقت دخول اللاعبين مع وقت إضافة المعلومات</div>
            <div className={styles.pageSubtitle}>
              اختر بطولة لعرض وقت دخول اللاعبين المطلوب مقابل أول وقت تمت فيه
              إضافة معلومات الغرفة لكل جدول، مع المنفذ المسؤول عنها.
            </div>
          </div>

          <div className={styles.toolbarControls}>
            <SelectInput
              className={styles.tournamentSelect}
              value={selectedTournamentId}
              setValue={handleTournamentChange}
              noPlaceHolder
              searchable
              searchThreshold={0}
              searchPlaceholder="ابحث عن بطولة"
              emptyResultsLabel="لا توجد بطولات مطابقة"
              disabled={!hasTournamentOptions}
            >
              {data.tournamentOptions.map((option) => (
                <option key={option.tournamentId} value={option.tournamentId}>
                  {formatTournamentOptionLabel(option)}
                </option>
              ))}
            </SelectInput>
          </div>
        </div>

        {isInitialLoading ? (
          <div className={styles.loaderContainer}>
            <Loader />
          </div>
        ) : !hasTournamentOptions ? (
          <div className={styles.placeholderCard}>
            لا توجد بطولات متاحة لعرض هذا التقرير حالياً.
          </div>
        ) : (
          <>
            {fetchStatus === "failed" ? (
              <div className={styles.errorBanner}>{errorMessage}</div>
            ) : null}

            {fetchStatus === "loading" && hasRows ? (
              <div className={styles.loadingBanner}>جارٍ تحديث التقرير...</div>
            ) : null}

            <div className={styles.summaryGrid}>
              {summaryCards.map((card) => (
                <div key={card.key} className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>{card.label}</div>
                  <div className={styles.summaryValue}>
                    {formatNumber(card.value)}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.tableCard}>
              <div className={styles.tableCardHeader}>
                <div className={styles.tableCardTitle}>تفاصيل الغرف</div>
                <div className={styles.tableCardMetaGroup}>
                  <div className={styles.tableCardMeta}>
                    البطولة المحددة:{" "}
                    {formatTournamentOptionLabel(data.selected || {})}
                  </div>
                  <div className={styles.tableCardMeta}>
                    النتائج الظاهرة: {formatNumber(filteredRows.length)} /{" "}
                    {formatNumber(data.rows.length)}
                  </div>
                </div>
              </div>

              <div className={styles.filtersBar}>
                <div className={styles.filterField}>
                  <div className={styles.filterLabel}>اسم الجدول</div>
                  <SelectInput
                    className={styles.filterSelect}
                    value={tableNameFilter}
                    setValue={setTableNameFilter}
                    placeHolder="كل الجداول"
                    searchable
                    searchThreshold={0}
                    searchPlaceholder="ابحث باسم الجدول"
                    emptyResultsLabel="لا توجد جداول مطابقة"
                  >
                    {tableNameOptions.map((tableName) => (
                      <option key={tableName} value={tableName}>
                        {tableName}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className={styles.filterField}>
                  <div className={styles.filterLabel}>اسم منفذ الجدول</div>
                  <SelectInput
                    className={styles.filterSelect}
                    value={executorNameFilter}
                    setValue={setExecutorNameFilter}
                    placeHolder="كل المنفذين"
                    searchable
                    searchThreshold={0}
                    searchPlaceholder="ابحث باسم المنفذ"
                    emptyResultsLabel="لا توجد أسماء مطابقة"
                  >
                    {executorNameOptions.map((executorName) => (
                      <option key={executorName} value={executorName}>
                        {executorName}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className={styles.filterField}>
                  <div className={styles.filterLabel}>المنفذ</div>
                  <SelectInput
                    className={styles.filterSelect}
                    value={setNumberFilter}
                    setValue={setSetNumberFilter}
                    placeHolder="كل المنفذين"
                    searchable
                    searchThreshold={0}
                    searchPlaceholder="ابحث برقم المنفذ"
                    emptyResultsLabel="لا توجد أرقام مطابقة"
                  >
                    {setNumberOptions.map((setNumber) => (
                      <option key={setNumber} value={String(setNumber)}>
                        {setNumber >= 10000 ? "منفذ الفاينل" : setNumber >= 1000 ? "منفذ المرحله المتقدمه" : `المنفذ رقم ${formatNumber(setNumber)}`}
                      </option>
                    ))}
                  </SelectInput>
                </div>

                <div className={styles.filterField}>
                  <div className={styles.filterLabel}>الحالة</div>
                  <SelectInput
                    className={styles.filterSelect}
                    value={statusFilter}
                    setValue={setStatusFilter}
                    placeHolder="كل الحالات"
                    searchable
                    searchThreshold={0}
                    searchPlaceholder="ابحث عن حالة"
                    emptyResultsLabel="لا توجد حالات مطابقة"
                  >
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </div>

              {!hasRows ? (
                <div className={styles.placeholderCard}>
                  لا توجد غرف مرتبطة بجدول زمني في البطولة المحددة حالياً.
                </div>
              ) : !hasFilteredRows ? (
                <div className={styles.placeholderCard}>
                  لا توجد نتائج مطابقة للفلاتر الحالية.
                </div>
              ) : (
                <div className={styles.tableScroller}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>رقم</th>
                        <th>الجدول</th>
                        <th>المنفذ</th>
                        <th>منفذ الجدول</th>
                        <th>وقت دخول اللاعبين المطلوب</th>
                        <th>وقت إضافة معلومات الغرفة</th>
                        <th>الفرق</th>
                        <th>الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row, index) => (
                        <tr key={row.tableId || `${row.setNumber}-${index}`}>
                          <td className={styles.numberCell}>
                            {formatNumber(index + 1)}
                          </td>
                          <td className={styles.tableNameCell}>
                            <div className={styles.primaryCellText}>
                              {row.tableName || "بدون اسم"}
                            </div>
                            <div className={styles.secondaryCellText}>
                              {getTableTypeLabel(row.tableType)}
                            </div>
                          </td>
                          <td>{row.setNumber >= 10000 ? "منفذ الفاينل" : row.setNumber >= 1000 ? "منفذ المرحله المتقدمه" : `المنفذ رقم ${formatNumber(row.setNumber)}`}</td>
                          <td>{row.coLeaderLabel || "غير محدد"}</td>
                          <td>{formatDateTime(row.roomEntryAt)}</td>
                          <td>{formatDateTime(row.roomInformationAddedAt)}</td>
                          <td className={styles.differenceCell}>
                            {formatDeltaLabel(row.deltaSeconds)}
                          </td>
                          <td>
                            <span
                              className={`${styles.statusPill} ${
                                styles[`statusPill_${row.status}`]
                              }`}
                            >
                              {getStatusLabel(row.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SettingsPagesContainer>
  );
};

export default RoomInformationEfficiency;
